// @flow
import { useDispatch } from 'react-redux';

import {
  setCartProductOwner,
  getCart,
  updateCart,
  selectCartUUID,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { setCurrentId } from '@gandi/redux-sharing';
import { useTranslator } from '@gandi/translate';

import { logging } from 'gandi.redux-logger';

import { useCartSummaryContext } from 'src/Layout/context/CartSummary';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { applyTemplateIfNeeded } from 'src/modules/app/actions';
import type { Dispatch, GetState } from 'src/types';

import { updateProductsPayload } from '../../AccountChoice/modules/actions';
import { getSetupOverviewUrl } from '../../Setup/modules/helpers';
import { selectHasDomainTransfer } from '../../Setup/modules/selectors';

export function useContinueClick(): ({
  /** sharingId */
  orgId: string,
  /** reload the cart setting the id as cart owner */
  reload: boolean,
}) => Promise<void> {
  const translator = useTranslator();
  const dispatch = useDispatch();
  const logger = logging(dispatch, 'AccountForm');
  const hasDomainTransfer = useAppSelector(selectHasDomainTransfer);
  const { setFrozen, setSubmitting, updateSharingIdContext } =
    useCartSummaryContext();

  /**
   * Go to overview if user wants the same address
   * for invoice and products owner.
   */
  async function handleContinueClick({
    orgId,
    reload,
  }: {
    orgId: string,
    reload: boolean,
  }): Promise<void> {
    setFrozen(true);
    setSubmitting(true);

    try {
      await dispatch(applyTemplateIfNeeded());
      await Promise.allSettled([
        // Needed to use selectCartProductOwner => getBaseContact => getProductsWithNewContactPayload => updateProductsPayload
        dispatch(setCartProductOwner(orgId)),
        dispatch(updateProductsPayload({ translator })),
      ]);

      const cartId = dispatch((_: Dispatch, getState: GetState) =>
        selectCartUUID(getState()),
      );

      try {
        await dispatch(
          updateCart(
            cartId,
            { owner_uuid: orgId, has_orga: true },
            { headers: { 'X-Dry-Run': '0' } },
          ),
        );
        // TODO: check why we setCurrentId after the get cart
        await dispatch(getCart()).then(() => dispatch(setCurrentId(orgId)));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        // pass
      }

      /// TODO: check if we cannot replace this part with a navigateTo
      await updateSharingIdContext({
        newSharingId: orgId,
        route: hasDomainTransfer
          ? getSetupOverviewUrl(cartId, 'transfer')
          : `/cart/${cartId}/overview`,
        reload,
      });
    } catch (err) {
      dispatch(notifyDanger(translator.__('Unexpected error occurred')));
      logger.error({ err }, 'Unable to update products payload.');
    } finally {
      setFrozen(false);
      setSubmitting(false);
    }
  }

  return handleContinueClick;
}
